import { Form, Switch, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import PolicySettingsPage, {
  DiffRow,
  fmtBool,
} from "pages/settings/policy/PolicySettingsPage";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";
import { translate } from "shared/languages";
import { AdminCapability } from "shared/utils/capabilities";
import { formatPhone } from "shared/utils/phone";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

type Values = {
  whatsapp_enabled: boolean;
  whatsapp_number?: string;
};

// The number the client app's WhatsApp buttons open (Home, the class page, the
// product page, discount vouchers), with a switch that hides those buttons
// altogether. Until this page existed the buttons used the registration phone
// Spott enters for the studio, which only a system admin could change and
// which is often the owner's own phone (System Review X2).
//
// The tax page's shape: one switch with its explanation under the label, and
// the dependent field below it while on. The number field unmounts while the
// switch is off; AntD keeps its value in the form store and the server keeps
// the stored number, so switching back on brings the same number back. The
// switch is deliberately not an in-page warning: the label's note already
// says what clients lose while it is off.
const WhatsappContactPage = () => {
  const [form] = useForm<Values>();
  const enabled = Form.useWatch("whatsapp_enabled", form);
  const toggleLabel = translate("class_settings.whatsapp.toggle");
  const numberLabel = translate("class_settings.whatsapp.number_label");

  return (
    <PolicySettingsPage<Values>
      // Same Pembatasan Admin group as payment methods and notifications:
      // the server's POLICY_KEY_CAPABILITY maps both keys to it.
      capability={AdminCapability.OperationalSettings}
      title={translate("class_settings.whatsapp.title")}
      confirmNote={translate(
        enabled === false
          ? "class_settings.confirm.note_whatsapp_off"
          : "class_settings.confirm.note_whatsapp_on",
      )}
      form={form}
      initialValues={(s) => ({
        whatsapp_enabled: s.whatsapp_enabled,
        whatsapp_number: s.whatsapp_number ?? undefined,
      })}
      buildSave={(v, s) => {
        const isEnabled = !!v.whatsapp_enabled;
        const nextNumber = v.whatsapp_number ?? s.whatsapp_number ?? "";
        const rows: DiffRow[] = [];
        if (isEnabled !== s.whatsapp_enabled) {
          rows.push({
            label: toggleLabel,
            before: fmtBool(s.whatsapp_enabled),
            after: fmtBool(isEnabled),
          });
        }
        // The number only matters while the buttons show: a change made
        // while off is neither listed nor sent, and the stored value is kept
        // (the notification-threshold rule).
        if (isEnabled && nextNumber !== (s.whatsapp_number ?? "")) {
          rows.push({
            label: numberLabel,
            before: s.whatsapp_number ? formatPhone(s.whatsapp_number) : "—",
            after: formatPhone(nextNumber),
          });
        }
        return {
          rows,
          payload: {
            whatsapp_enabled: isEnabled,
            ...(isEnabled ? { whatsapp_number: nextNumber } : {}),
          },
        };
      }}
    >
      <Form.Item label={toggleLabel}>
        <Text
          type="secondary"
          style={{ display: "block", fontSize: textPx(12.5), marginBottom: 8 }}
        >
          {translate("class_settings.whatsapp.toggle_desc")}
        </Text>
        <Form.Item name="whatsapp_enabled" valuePropName="checked" noStyle>
          <Switch aria-label={toggleLabel} />
        </Form.Item>
      </Form.Item>

      {enabled && (
        <Form.Item
          name="whatsapp_number"
          label={numberLabel}
          // The hint sits behind the label's tooltip, like the payroll
          // frequency and discount-code end-date labels (Andrew, 2026-10-01).
          tooltip={translate("class_settings.whatsapp.number_hint")}
          // Any country, mobile only, like every other phone field. No
          // smsOtp: nothing is ever sent to this number by Spott.
          rules={phoneRules({ required: true })}
          style={{ maxWidth: 360 }}
        >
          <PhoneInput />
        </Form.Item>
      )}
    </PolicySettingsPage>
  );
};

export default WhatsappContactPage;
